import React from "react";
import { SummaryCardData } from "../types";
import { WarningIcon } from "./Icons";

interface SummaryCardProps {
  data: SummaryCardData;
  onClick?: () => void;
  isActive?: boolean;
}

const SummaryCard: React.FC<SummaryCardProps> = ({
  data,
  onClick,
  isActive = false,
}) => {
  const getVariantStyles = () => {
    switch (data.variant) {
      case "high-risk":
        return {
          cardBg: "bg-[#FEF2F2]",
          iconBg: "bg-[#EF4444]",
          titleColor: "text-[#991B1B]",
          countColor: "text-[#B91C1C]",
          descColor: "text-[#DC2626]",
          border: "border-[#FECACA]", // light pink — inactive
          activeBorder: "border-[#FCA5A5]", // slightly deeper pink — active
          activeRing: "0 0 0 2px rgba(239,68,68,0.25)",
          badgeBg: "bg-[#EF4444]",
        };
      case "upcoming":
        return {
          cardBg: "bg-[#FFFBEB]",
          iconBg: "bg-[#F59E0B]",
          titleColor: "text-[#92400E]",
          countColor: "text-[#B45309]",
          descColor: "text-[#D97706]",
          border: "border-[#FDE68A]", // light amber — inactive
          activeBorder: "border-[#FCD34D]", // slightly deeper amber — active
          activeRing: "0 0 0 2px rgba(245,158,11,0.25)",
          badgeBg: "bg-[#F59E0B]",
        };
      case "needs-attention":
        return {
          cardBg: "bg-[#FFF7ED]",
          iconBg: "bg-[#F97316]",
          titleColor: "text-[#7C2D12]",
          countColor: "text-[#9A3412]",
          descColor: "text-[#EA580C]",
          border: "border-[#FED7AA]", // light orange — inactive
          activeBorder: "border-[#FDBA74]", // slightly deeper orange — active
          activeRing: "0 0 0 2px rgba(249,115,22,0.25)",
          badgeBg: "bg-[#F97316]",
        };
      default:
        return {
          cardBg: "bg-[#F9FAFB]",
          iconBg: "bg-[#9CA3AF]",
          titleColor: "text-[#374151]",
          countColor: "text-[#111827]",
          descColor: "text-[#6B7280]",
          border: "border-[#E5E7EB]",
          activeBorder: "border-[#D1D5DB]",
          activeRing: "0 0 0 2px rgba(156,163,175,0.3)",
          badgeBg: "bg-[#9CA3AF]",
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <div
      onClick={onClick}
      role={onClick ? "button" : undefined}
      aria-pressed={onClick ? isActive : undefined}
      style={
        isActive
          ? { boxShadow: `${styles.activeRing}, 0 4px 12px rgba(0,0,0,0.08)` }
          : undefined
      }
      className={[
        styles.cardBg,
        "border rounded-xl p-5 transition-all select-none",
        isActive
          ? `${styles.activeBorder} border-2`
          : `${styles.border} border`,
        onClick ? "cursor-pointer" : "",
        !isActive && onClick ? "hover:shadow-md hover:border-opacity-80" : "",
      ].join(" ")}
    >
      <div className="flex items-center space-x-4">
        {/* Icon */}
        <div
          className={`${styles.iconBg} rounded-full h-10 w-10 flex items-center justify-center flex-shrink-0`}
        >
          <WarningIcon className="h-5 w-5 text-white" />
        </div>

        {/* Text */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between mb-0.5">
            <p className={`text-sm font-semibold ${styles.titleColor}`}>
              {data.title}
            </p>
            {isActive && (
              <span
                className={`${styles.badgeBg} text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none`}
              >
                ACTIVE
              </span>
            )}
          </div>
          <p
            className={`text-3xl font-bold ${styles.countColor} leading-none mb-1`}
          >
            {data.count}
          </p>
          <p className={`text-xs ${styles.descColor}`}>
            {isActive ? "Click to clear filter" : data.description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SummaryCard;
